import React, { useState, useEffect } from "react";
import axios from "axios";
import { database } from "../../firebaseConfig";
import { ref, get, set } from "firebase/database";
import "./FloatingIconsConfigEditor.css";

import { config } from '../../config/config'
const API_URL = config.baseUrl;

function deleteFile(fileUrl) {
    if (!fileUrl) return;
    const parts = fileUrl.split("/");
    const filename = parts[parts.length - 1];
    return axios
        .delete(`${API_URL}delete`, { data: { filename } })
        .then(() => {
            console.log("Deleted file:", filename);
        })
        .catch((error) => {
            console.error("Error deleting file:", error);
        });
}

const FloatingIconsConfigEditor = () => {
    // Default configuration includes socialMedia (empty array) and SEO metadata.
    const defaultConfig = {
        phone: { visible: true, number: "1234567890", showOnContactPage: true },
        whatsapp: { visible: true, number: "1234567890", showOnContactPage: true },
        email: { visible: true, address: "admin@example.com", showOnContactPage: true },
        scan: { visible: true, qrImageUrl: "", showOnContactPage: true },
        inquire: {
            visible: true,
            recipientEmail: "adityasp193@gmail.com",
            formToken: "3afcfb95-7497-48d2-b443-97875fe0c237",
            imageUrl: "",
            showOnContactPage: true,
        },
        seo: {
            metaDescription: "",
            metaKeywords: "",
        },
        socialMedia: [],
    };

    const [config, setConfig] = useState(defaultConfig);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const snapshot = await get(ref(database, "floatingIconsConfig"));
                if (snapshot.exists()) {
                    // Merge fetched config with defaults.
                    setConfig({ ...defaultConfig, ...snapshot.val() });
                } else {
                    setConfig(defaultConfig);
                }
            } catch (error) {
                console.error("Error fetching floating icons config:", error);
                setConfig(defaultConfig);
            }
            setLoading(false);
        };
        fetchConfig();
    }, []);

    // Upload file helper for image files.
    const uploadFile = async (file) => {
        if (!file) return "";
        const formData = new FormData();
        formData.append("media", file);
        try {
            const response = await axios.post(`${API_URL}upload`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            return response.data.fileUrl || "";
        } catch (error) {
            console.error("Error uploading file:", error);
            return "";
        }
    };

    const handleQrFileChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            const newUrl = await uploadFile(file);
            setConfig((prev) => ({
                ...prev,
                scan: { ...prev.scan, qrImageUrl: newUrl },
            }));
        }
    };

    // Handlers for social media contacts.
    const handleSocialMediaChange = (index, field, value) => {
        const updated = (config.socialMedia || []).map((item, i) =>
            i === index ? { ...item, [field]: value } : item
        );
        setConfig((prev) => ({ ...prev, socialMedia: updated }));
    };

    const handleSocialMediaIconChange = async (index, e) => {
        const file = e.target.files[0];
        if (file) {
            // Delete old icon if exists.
            if (config.socialMedia && config.socialMedia[index] && config.socialMedia[index].iconUrl) {
                await deleteFile(config.socialMedia[index].iconUrl);
            }
            const newUrl = await uploadFile(file);
            handleSocialMediaChange(index, "iconUrl", newUrl);
        }
    };

    const addSocialMediaContact = () => {
        const newContact = {
            platform: "",
            url: "",
            visible: true,
            showOnContactPage: true,
            iconUrl: "",
        };
        setConfig((prev) => ({
            ...prev,
            socialMedia: [...(prev.socialMedia || []), newContact],
        }));
    };

    const deleteSocialMediaContact = (index) => {
        const updated = (config.socialMedia || []).filter((_, i) => i !== index);
        setConfig((prev) => ({ ...prev, socialMedia: updated }));
    };

    const handleSaveConfig = async () => {
        try {
            await set(ref(database, "floatingIconsConfig"), config);
            alert("Floating icons configuration saved successfully!");
        } catch (error) {
            console.error("Error saving floating icons config:", error);
            alert("Error saving configuration.");
        }
    };

    if (loading)
        return (
            <div className="FloatingIconsConfigEditor-loading">
                Loading configuration...
            </div>
        );

    return (
        <div className="FloatingIconsConfigEditor">
            <h2 className="FloatingIconsConfigEditor-title">
                Edit Floating Icons Configuration
            </h2>
            <div className="FloatingIconsConfigEditor-form">
                {Object.keys(config).map((key) =>
                    key !== "seo" && key !== "socialMedia" ? (
                        <div key={key} className="FloatingIconsConfigEditor-section">
                            <div className="FloatingIconsConfigEditor-section-header">
                                <h3>{key.charAt(0).toUpperCase() + key.slice(1)}</h3>
                            </div>
                            <div className="FloatingIconsConfigEditor-section-body">
                                <div className="FloatingIconsConfigEditor-checkbox-group">
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={config[key].visible}
                                            onChange={(e) =>
                                                setConfig((prev) => ({
                                                    ...prev,
                                                    [key]: {
                                                        ...prev[key],
                                                        visible: e.target.checked,
                                                    },
                                                }))
                                            }
                                        />
                                        Display {key.charAt(0).toUpperCase() + key.slice(1)} Icon
                                    </label>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={config[key].showOnContactPage}
                                            onChange={(e) =>
                                                setConfig((prev) => ({
                                                    ...prev,
                                                    [key]: {
                                                        ...prev[key],
                                                        showOnContactPage: e.target.checked,
                                                    },
                                                }))
                                            }
                                        />
                                        Show on Contact Page
                                    </label>
                                </div>
                                {key === "phone" || key === "whatsapp" ? (
                                    <div className="FloatingIconsConfigEditor-input-group">
                                        <label>
                                            {key === "phone"
                                                ? "Phone Number:"
                                                : "WhatsApp Number:"}
                                        </label>
                                        <input
                                            type="text"
                                            value={config[key].number}
                                            onChange={(e) =>
                                                setConfig((prev) => ({
                                                    ...prev,
                                                    [key]: {
                                                        ...prev[key],
                                                        number: e.target.value,
                                                    },
                                                }))
                                            }
                                        />
                                    </div>
                                ) : key === "email" ? (
                                    <div className="FloatingIconsConfigEditor-input-group">
                                        <label>Email Address:</label>
                                        <input
                                            type="email"
                                            value={config[key].address}
                                            onChange={(e) =>
                                                setConfig((prev) => ({
                                                    ...prev,
                                                    [key]: {
                                                        ...prev[key],
                                                        address: e.target.value,
                                                    },
                                                }))
                                            }
                                        />
                                    </div>
                                ) : key === "inquire" ? (
                                    <>
                                        <div className="FloatingIconsConfigEditor-input-group">
                                            <label>Recipient Email:</label>
                                            <input
                                                type="email"
                                                value={config[key].recipientEmail}
                                                onChange={(e) =>
                                                    setConfig((prev) => ({
                                                        ...prev,
                                                        [key]: {
                                                            ...prev[key],
                                                            recipientEmail: e.target.value,
                                                        },
                                                    }))
                                                }
                                            />
                                        </div>
                                        <div className="FloatingIconsConfigEditor-input-group">
                                            <label>Form Token:</label>
                                            <input
                                                type="text"
                                                value={config[key].formToken}
                                                onChange={(e) =>
                                                    setConfig((prev) => ({
                                                        ...prev,
                                                        [key]: {
                                                            ...prev[key],
                                                            formToken: e.target.value,
                                                        },
                                                    }))
                                                }
                                            />
                                        </div>
                                        <div className="FloatingIconsConfigEditor-input-group">
                                            <label>Inquiry Image:</label>
                                            {config.inquire.imageUrl && (
                                                <div className="FloatingIconsConfigEditor-preview-group">
                                                    <img
                                                        src={config.inquire.imageUrl}
                                                        alt="Inquiry"
                                                        className="FloatingIconsConfigEditor-preview-image"
                                                    />
                                                    <button
                                                        className="FloatingIconsConfigEditor-delete-button"
                                                        onClick={() => {
                                                            deleteFile(config.inquire.imageUrl);
                                                            setConfig((prev) => ({
                                                                ...prev,
                                                                inquire: {
                                                                    ...prev.inquire,
                                                                    imageUrl: "",
                                                                },
                                                            }));
                                                        }}
                                                    >
                                                        Delete Image
                                                    </button>
                                                </div>
                                            )}
                                            <input
                                                type="file"
                                                accept="image/*"
                                                className="FloatingIconsConfigEditor-input-file"
                                                onChange={async (e) => {
                                                    const file = e.target.files[0];
                                                    if (file) {
                                                        const newUrl = await uploadFile(file);
                                                        setConfig((prev) => ({
                                                            ...prev,
                                                            inquire: {
                                                                ...prev.inquire,
                                                                imageUrl: newUrl,
                                                            },
                                                        }));
                                                    }
                                                }}
                                            />
                                        </div>
                                    </>
                                ) : key === "scan" ? (
                                    <div className="FloatingIconsConfigEditor-input-group">
                                        <label>QR Code Image:</label>
                                        {config.scan.qrImageUrl && (
                                            <div className="FloatingIconsConfigEditor-preview-group">
                                                <img
                                                    src={config.scan.qrImageUrl}
                                                    alt="QR Code"
                                                    className="FloatingIconsConfigEditor-preview-image"
                                                />
                                                <button
                                                    className="FloatingIconsConfigEditor-delete-button"
                                                    onClick={() => {
                                                        deleteFile(config.scan.qrImageUrl);
                                                        setConfig((prev) => ({
                                                            ...prev,
                                                            scan: {
                                                                ...prev.scan,
                                                                qrImageUrl: "",
                                                            },
                                                        }));
                                                    }}
                                                >
                                                    Delete Image
                                                </button>
                                            </div>
                                        )}
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="FloatingIconsConfigEditor-input-file"
                                            onChange={handleQrFileChange}
                                        />
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    ) : null
                )}
                {/* SEO Metadata Section */}
                <div className="FloatingIconsConfigEditor-section">
                    <div className="FloatingIconsConfigEditor-section-header">
                        <h3>SEO Metadata</h3>
                    </div>
                    <div className="FloatingIconsConfigEditor-section-body">
                        <div className="FloatingIconsConfigEditor-input-group">
                            <label>Meta Description:</label>
                            <textarea
                                className="FloatingIconsConfigEditor-textarea"
                                value={config.seo?.metaDescription || ""}
                                onChange={(e) =>
                                    setConfig((prev) => ({
                                        ...prev,
                                        seo: {
                                            ...prev.seo,
                                            metaDescription: e.target.value,
                                        },
                                    }))
                                }
                                placeholder="Enter meta description..."
                            />
                        </div>
                        <div className="FloatingIconsConfigEditor-input-group">
                            <label>Meta Keywords:</label>
                            <input
                                type="text"
                                className="FloatingIconsConfigEditor-input"
                                value={config.seo?.metaKeywords || ""}
                                onChange={(e) =>
                                    setConfig((prev) => ({
                                        ...prev,
                                        seo: {
                                            ...prev.seo,
                                            metaKeywords: e.target.value,
                                        },
                                    }))
                                }
                                placeholder="Enter comma separated keywords..."
                            />
                        </div>
                    </div>
                </div>
                {/* Social Media Contacts Section */}
                <div className="FloatingIconsConfigEditor-section">
                    <div className="FloatingIconsConfigEditor-section-header">
                        <h3>Social Media Contacts</h3>
                    </div>
                    <div className="FloatingIconsConfigEditor-section-body">
                        {(config.socialMedia || []).map((item, index) => (
                            <div key={index} className="FloatingIconsConfigEditor-social-item">
                                <div className="FloatingIconsConfigEditor-input-group">
                                    <label>Platform:</label>
                                    <input
                                        type="text"
                                        value={item.platform || ""}
                                        onChange={(e) =>
                                            handleSocialMediaChange(index, "platform", e.target.value)
                                        }
                                        placeholder="e.g., Facebook, Instagram"
                                    />
                                </div>
                                <div className="FloatingIconsConfigEditor-input-group">
                                    <label>URL:</label>
                                    <input
                                        type="text"
                                        value={item.url || ""}
                                        onChange={(e) =>
                                            handleSocialMediaChange(index, "url", e.target.value)
                                        }
                                        placeholder="Enter URL"
                                    />
                                </div>
                                <div className="FloatingIconsConfigEditor-checkbox-group">
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={item.visible || false}
                                            onChange={(e) =>
                                                handleSocialMediaChange(index, "visible", e.target.checked)
                                            }
                                        />
                                        Display Icon
                                    </label>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={item.showOnContactPage || false}
                                            onChange={(e) =>
                                                handleSocialMediaChange(index, "showOnContactPage", e.target.checked)
                                            }
                                        />
                                        Show on Contact Page
                                    </label>
                                </div>
                                <div className="FloatingIconsConfigEditor-input-group">
                                    <label>Icon:</label>
                                    {item.iconUrl && (
                                        <div className="FloatingIconsConfigEditor-preview-group">
                                            <img
                                                src={item.iconUrl}
                                                alt={item.platform}
                                                className="FloatingIconsConfigEditor-preview-image"
                                            />
                                            <button
                                                className="FloatingIconsConfigEditor-delete-button"
                                                onClick={() => {
                                                    deleteFile(item.iconUrl);
                                                    handleSocialMediaChange(index, "iconUrl", "");
                                                }}
                                            >
                                                Delete Icon
                                            </button>
                                        </div>
                                    )}
                                    <input
                                        type="file"
                                        accept="image/*"
                                        className="FloatingIconsConfigEditor-input-file"
                                        onChange={(e) => handleSocialMediaIconChange(index, e)}
                                    />
                                </div>
                                <button
                                    className="FloatingIconsConfigEditor-delete-button"
                                    onClick={() => deleteSocialMediaContact(index)}
                                >
                                    Delete Social Media Contact
                                </button>
                            </div>
                        ))}
                        <button
                            className="FloatingIconsConfigEditor-add-button"
                            onClick={addSocialMediaContact}
                        >
                            Add Social Media Contact
                        </button>
                    </div>
                </div>
                <button
                    className="FloatingIconsConfigEditor-save-button"
                    onClick={handleSaveConfig}
                >
                    Save Configuration
                </button>
            </div>
        </div>
    );
};

export default FloatingIconsConfigEditor;
